import Paper from "@mui/material/Paper";
import type {
    ForwardedRef,
    KeyboardEvent as ReactKeyboardEvent,
    MouseEvent as ReactMouseEvent,
    ReactElement,
    Ref,
} from "react";
import {forwardRef, useId, useImperativeHandle, useMemo, useRef} from "react";
import {FLOATING_SHADOW} from "../shared/floatingShadow";
import VariableInfoCard from "./info/VariableInfoCard";
import useInfoCard from "./info/useInfoCard";
import PickerList, {type PickerListHandle} from "./list/PickerList";
import pickerEntries from "./list/pickerEntries";
import useVariableListNavigation from "./list/useVariableListNavigation";
import PickerFooterHints from "./PickerFooterHints";
import PickerSearchHeader from "./PickerSearchHeader";
import type {PickerItem} from "./pickerItem";

export type PickerKeyEvent = Pick<
    KeyboardEvent,
    "key" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey"
>;

export interface VariablePickerHandle {
    /** True when the picker consumed the key, so the host can stop there. */
    handleKey: (event: PickerKeyEvent) => boolean;
}

export interface VariablePickerPanelProps<I extends PickerItem = PickerItem> {
    /** Already filtered; the panel never filters. */
    items: readonly I[];
    /** What the author has typed; in "/" mode the document owns the text, not a field in here. */
    query: string;
    onSelect: (item: I) => void;
    onClose: () => void;
    /** Set → the header is a real input and the panel's mount owns the query. */
    onQueryChange?: (query: string) => void;
    /** The header that shows the query. Off where the query is already visible in the document. */
    search?: boolean;
    emptyLabel?: string;
    width?: number;
}

/** The keys an owned-query input hands to the list instead of moving its caret. */
const LIST_KEYS = new Set(["ArrowUp", "ArrowDown", "Enter", "Tab"]);

const hasModifier = (event: PickerKeyEvent) => event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;

function VariablePickerPanelInner<I extends PickerItem>(
    {
        items,
        query,
        onSelect,
        onClose,
        onQueryChange,
        search = true,
        emptyLabel,
        width = 320,
    }: VariablePickerPanelProps<I>,
    ref: ForwardedRef<VariablePickerHandle>,
) {
    const listboxId = useId();
    const listRef = useRef<PickerListHandle>(null);

    const {entries, rows, rowEntryIndex} = useMemo(() => pickerEntries(items), [items]);
    const {root, setRoot, info, openInfo, closeInfoSoon, cancelClose, closeInfo} = useInfoCard<I>(query);

    const {selectedIndex, selectFromPointer, handleKey} = useVariableListNavigation({
        items: rows,
        onSelect,
        resetKey: query,
        rowEntryIndex,
        scrollToIndex: entryIndex => listRef.current?.scrollToIndex(entryIndex),
    });

    const optionId = (item: I) => `${listboxId}-option-${item.key.replace(/\s+/g, "-")}`;
    const selectedItem = rows[selectedIndex];

    /** Escape belongs to the card while one is open; the popup's own closing is its mount's. */
    const handlePanelKey = (event: PickerKeyEvent): boolean => {
        if (event.key === "Escape") {
            if (!info) return false;

            closeInfo();
            return true;
        }

        if (hasModifier(event)) return false;

        return handleKey(event.key);
    };

    useImperativeHandle(ref, () => ({handleKey: handlePanelKey}));

    /*
        In owned-query mode focus sits in the header's input, so its keys arrive here rather than
        through a host. Escape is stopped either way, so a host Dialog stays open behind the popup.
     */
    const handleInputKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            if (!handlePanelKey(event)) onClose();
            return;
        }

        if (!LIST_KEYS.has(event.key) || hasModifier(event)) return;

        if (handleKey(event.key)) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    /*
        Focus stays where the keys are read: the editor in "/" mode, the search input in owned-query
        mode. A pointer down anywhere else in the popup (a heading, the footer, the padding) would
        otherwise take focus, and arrows, Enter and typing would stop reaching the list while it stays
        open. The info card keeps its text selectable, and the input still takes a click for its caret.
     */
    const keepFocus = (event: ReactMouseEvent<HTMLDivElement>) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest("[data-variable-info-card]")) return;
        if (onQueryChange && target?.closest("input")) return;

        event.preventDefault();
    };

    return (
        <Paper
            ref={setRoot}
            variant="outlined"
            onMouseDown={keepFocus}
            sx={{width, maxWidth: "calc(100vw - 32px)", borderRadius: 2, boxShadow: FLOATING_SHADOW}}
        >
            {search && (
                <PickerSearchHeader
                    query={query}
                    onClose={onClose}
                    onQueryChange={onQueryChange}
                    onKeyDown={onQueryChange ? handleInputKey : undefined}
                    activeDescendant={selectedItem ? optionId(selectedItem) : undefined}
                    listboxId={listboxId}
                />
            )}

            <PickerList
                ref={listRef}
                entries={entries}
                query={query}
                selectedIndex={selectedIndex}
                listboxId={listboxId}
                optionId={optionId}
                infoKey={info?.item.key ?? null}
                emptyLabel={emptyLabel}
                width={width}
                onSelect={onSelect}
                onPointerSelect={selectFromPointer}
                onOpenInfo={openInfo}
                onCloseInfoSoon={closeInfoSoon}
                onScroll={closeInfo}
            />

            <VariableInfoCard
                info={info}
                container={root}
                onClickAway={closeInfo}
                onMouseEnter={cancelClose}
                onMouseLeave={closeInfoSoon}
            />

            <PickerFooterHints/>
        </Paper>
    );
}

/**
 * The variable popup: the query, the rows grouped by scope, a card per row and the key legend.
 *
 * It never takes focus in "/" mode — the editor keeps the caret and hands keys over through the
 * handle. With `onQueryChange` it is a self-contained search instead, its input in the same frame.
 */
export const VariablePickerPanel = forwardRef(VariablePickerPanelInner) as <I extends PickerItem = PickerItem>(
    props: VariablePickerPanelProps<I> & {ref?: Ref<VariablePickerHandle>},
) => ReactElement | null;

export default VariablePickerPanel;
